import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import {
  getUserProfile,
  updateUserProfile,
  getCountries,
  getInstitutes,
} from "../api/Api";
import { showToast } from "../utils/toastUtils";
import { Container, Box, Paper, Grid } from "@mui/material";
import CustomButton from "./CustomButton";
import ShortTextInput from "./Inputs/ShortTextInput";
import TextAreaInput from "./Inputs/TextAreaInput";
import AutoCompleteDropdown from "../components/Inputs/AutoCompleteDropdown";
import { USER_EXPERIENCE } from "../constants/constant";
import "./UserProfileComponent.module.css";
import { UpdateUserSchema } from "../utils/validations";

function UpdateProfile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [institutes, setInstitutes] = useState([]);
  const [initialValues, setInitialValues] = useState({
    username: "",
    first_name: "",
    last_name: "",
    jobTitle: "",
    country: "",
    department: "",
    experience: "",
    institute: "",
    instituteId: "",
    website: "",
    aboutYou: "",
    institute_name: "",
  });

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const response = await getUserProfile();
        if (response.status === 200) {
          const data = response.data;
          let experienceValue = "";
          if (typeof data.experience === "string") {
            try {
              if (data.experience.includes("{")) {
                const formattedExp = data.experience.replace(/'/g, '"');
                const parsedExp = JSON.parse(formattedExp);
                experienceValue = parsedExp.value || "";
              } else {
                experienceValue = data.experience;
              }
            } catch {
              experienceValue = "";
            }
          } else if (data.experience && typeof data.experience === "object") {
            experienceValue = data.experience.value || "";
          }

          setInitialValues({
            username: data.username || "",
            first_name: data.firstname || "",
            last_name: data.lastname || "",
            jobTitle: data.jobTitle || "",
            country: data.country || "",
            department: data.department || "",
            experience: experienceValue,
            institute: data.institute?.name || "",
            instituteId: data.institute?.id || "",
            website: data.website || "",
            aboutYou: data.aboutYou || "",
            institute_name: data.institute?.name || "",
          });
        }
      } catch (error) {
        showToast("Failed to fetch user data", "error");
      } finally {
        setLoading(false);
      }
    };

    getCountries().then((response) => {
      if (response?.status === 200) setCountries(response.data);
    });

    getInstitutes().then((response) => {
      if (response?.status === 200) setInstitutes(response.data);
    });

    fetchUserData();
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      const selectedCountry = countries.find((c) => c.name === values.country);
      const countryCode = selectedCountry?.code || "";

      const formattedData = {
        username: values.username,
        FirstName: values.first_name,
        LastName: values.last_name,
        jobTitle: values.jobTitle,
        country_code: countryCode,
        department: values.department,
        experience: values.experience,
        institute_id: values.instituteId,
        institute_name: values.institute,
        website: values.website,
        aboutYou: values.aboutYou,
      };

      const response = await updateUserProfile(formattedData);
      if (response.status === 200) {
        showToast("Profile updated successfully", "success");
        navigate("/userProfile");
      }
    } catch (error) {
      showToast("Failed to update profile", "error");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        mt: 6,
        mb: 8,
        minHeight: "100vh",
        background: "#fafbfc",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--font-style-primary)",
        fontWeight: 600,
      }}
    >
      <Box sx={{ width: "100%", background: "#fafbfc" }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 4 },
            background: "#fafbfc",
            borderRadius: "16px",
            boxShadow: "none",
            maxWidth: 700,
            mx: "auto",
          }}
        >
          <h2
            style={{
              fontWeight: 700,
              marginBottom: 24,
              letterSpacing: 0.5,
              fontFamily: "var(--font-style-primary)",
              textAlign: "center",
              fontSize: "2rem",
            }}
          >
            Update Profile
          </h2>
          <Formik
            initialValues={initialValues}
            validationSchema={UpdateUserSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
              isSubmitting,
            }) => (
              <Form noValidate>
                {/* Username */}
                <ShortTextInput
                  id="username"
                  name="username"
                  label="User Name"
                  value={values.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  error={
                    touched.username && errors.username ? errors.username : ""
                  }
                  helperText={
                    touched.username && errors.username ? errors.username : ""
                  }
                />

                {/* First Name / Last Name */}
                <Grid container spacing={2} className="mb-3">
                  <Grid item xs={12} sm={6}>
                    <ShortTextInput
                      id="first_name"
                      name="first_name"
                      label="First Name"
                      value={values.first_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      error={
                        touched.first_name && errors.first_name
                          ? errors.first_name
                          : ""
                      }
                      helperText={
                        touched.first_name && errors.first_name
                          ? errors.first_name
                          : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ShortTextInput
                      id="last_name"
                      name="last_name"
                      label="Last Name"
                      value={values.last_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      error={
                        touched.last_name && errors.last_name
                          ? errors.last_name
                          : ""
                      }
                      helperText={
                        touched.last_name && errors.last_name
                          ? errors.last_name
                          : ""
                      }
                    />
                  </Grid>
                </Grid>

                {/* Country / Job Title */}
                <Grid container spacing={2} className="mb-3">
                  <Grid item xs={12} sm={6}>
                    <AutoCompleteDropdown
                      id="country"
                      label="Country"
                      disablePortal
                      options={countries}
                      getOptionLabel={(option) => option.name}
                      value={
                        countries.find((c) => c.name === values.country) || null
                      }
                      onChange={(event, value) =>
                        setFieldValue("country", value ? value.name : "")
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ShortTextInput
                      id="jobTitle"
                      name="jobTitle"
                      label="Job Title"
                      value={values.jobTitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      error={
                        touched.jobTitle && errors.jobTitle
                          ? errors.jobTitle
                          : ""
                      }
                      helperText={
                        touched.jobTitle && errors.jobTitle
                          ? errors.jobTitle
                          : ""
                      }
                    />
                  </Grid>
                </Grid>

                {/* Department */}
                <ShortTextInput
                  id="department"
                  name="department"
                  label="Department/Research Group"
                  value={values.department}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  className="mb-3"
                  error={
                    touched.department && errors.department
                      ? errors.department
                      : ""
                  }
                  helperText={
                    touched.department && errors.department
                      ? errors.department
                      : ""
                  }
                />

                {/* Experience */}
                <AutoCompleteDropdown
                  id="experience"
                  label="Experience"
                  options={USER_EXPERIENCE.map((e) => ({ name: e.duration }))}
                  value={
                    USER_EXPERIENCE.find(
                      (e) => e.duration === values.experience
                    )
                      ? { name: values.experience }
                      : null
                  }
                  onChange={(event, value) =>
                    setFieldValue("experience", value?.name || "")
                  }
                  placeholder="Select Duration"
                  fullWidth
                  className="mb-3"
                />

                {/* Institute */}
                <AutoCompleteDropdown
                  id="institute"
                  label="Institute"
                  disablePortal
                  options={institutes}
                  getOptionLabel={(option) => option.name}
                  value={
                    institutes.find((i) => i.name === values.institute) || null
                  }
                  onChange={(event, value) => {
                    setFieldValue("institute", value ? value.name : "");
                    setFieldValue("instituteId", value ? value.id : "");
                  }}
                  fullWidth
                  className="mb-3"
                />

                {/* Website */}
                <ShortTextInput
                  id="website"
                  name="website"
                  label="Website/Linkedin"
                  value={values.website}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  className="mb-3"
                  error={
                    touched.website && errors.website ? errors.website : ""
                  }
                  helperText={
                    touched.website && errors.website ? errors.website : ""
                  }
                />

                {/* About You */}
                <TextAreaInput
                  id="aboutYou"
                  name="aboutYou"
                  label="About You"
                  value={values.aboutYou}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  rows={3}
                  className="mb-4"
                  error={
                    touched.aboutYou && errors.aboutYou ? errors.aboutYou : ""
                  }
                  helperText={
                    touched.aboutYou && errors.aboutYou ? errors.aboutYou : ""
                  }
                />

                {/* Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <CustomButton
                    variant="outline"
                    onClick={() => navigate("/userProfile")}
                    className="px-5"
                  >
                    Back
                  </CustomButton>
                  <CustomButton
                    type="submit"
                    disabled={loading || isSubmitting}
                    className="px-5"
                  >
                    {loading || isSubmitting ? "Updating..." : "Update"}
                  </CustomButton>
                </Box>
              </Form>
            )}
          </Formik>
        </Paper>
      </Box>
    </Container>
  );
}

export default UpdateProfile;
